import { useState, useCallback } from 'react';
import type { SortOrder, SortState } from '../components/ui/tableTemplate/TableTemplateTypes';

type Phase = SortOrder | null;
//Hold the current sort phase for a column: 'asc', 'desc', or null (unsorted).
// Strategy map — drives the asc → desc → null cycle with no if/else.
const INITIAL_SORT_ORDER: SortOrder = 'asc';
const NEXT_SORT: Record<SortOrder, Phase> = { asc: 'desc', desc: null };

/**
 * Generic hook to handle table sorting state.
 * Logic:
 * 1. Different column clicked? -> Reset to 'asc'.
 * 2. Same column clicked? -> Cycle using NEXT_SORT strategy.
 *
 * `initial` (default: unsorted) shows a sort as active from the first render, e.g. the
 * server's default order (HOMCO-1413: profiles sorted by name ascending), so a first
 * click on that column reverses it.
 */
export function useSortState<T>(initial: SortState<T> | null = null) {
  const [sortState, setSortState] = useState<SortState<T> | null>(initial);

  const handleSortChange = useCallback((key: keyof T) => {
    setSortState(prev => {
      const current: Phase = prev?.sortBy === key ? prev.sortOrder : null;
      const next = current === null ? INITIAL_SORT_ORDER : NEXT_SORT[current];
      return next === null ? null : { sortBy: key, sortOrder: next };
    });
  }, []);

  return { sortState, handleSortChange };
}
